import React, { memo, useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import { TextField, TableCell } from "@mui/material";
import colors from "assets/theme-dark/base/colors";
import { formatMetric } from "../../utils/metricUtils";
import { AnalysisDriverType } from "../../types/types";
import { EditedMetrics } from "./DriverMetricsTableBody";
import { Theme } from "@mui/material/styles";

interface DriverMetricCellProps {
  uuid: string;
  field: "driver1Median" | "driver1Avg" | "driver1Percentile" | "driver2Median" | "driver2Avg" | "driver2Percentile";
  value: number | undefined;
  driverMode: AnalysisDriverType;
  editedPercentiles: EditedMetrics;
  setEditedPercentiles: React.Dispatch<React.SetStateAction<EditedMetrics>>;
  isEditing: boolean;
  setEditingCell: (cell: { uuid: string; field: string } | null) => void;
}

const DriverMetricCell = memo(
  ({
    uuid,
    field,
    value,
    driverMode,
    editedPercentiles,
    setEditedPercentiles,
    isEditing,
    setEditingCell,
  }: DriverMetricCellProps) => {
    const [localValue, setLocalValue] = useState(
      editedPercentiles[uuid]?.[field]?.toString() ?? value?.toString() ?? ""
    );

    useEffect(() => {
      setLocalValue(editedPercentiles[uuid]?.[field]?.toString() ?? value?.toString() ?? "");
    }, [editedPercentiles, uuid, field, value]);

    const handleMetricChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setLocalValue(event.target.value);
    };

    const handleBlur = () => {
      const cleanedValue = localValue.trim();
      const parsedValue = parseFloat(cleanedValue);
      const newValue = isNaN(parsedValue) || cleanedValue === "" ? 0 : parsedValue;

      setEditedPercentiles((prev) => ({
        ...prev,
        [uuid]: {
          ...prev[uuid],
          [field]: newValue,
        },
      }));
      setLocalValue(newValue.toString());
      setEditingCell(null);
    };

    const displayValue = editedPercentiles[uuid]?.[field] ?? value;

    return (
      <RegularTableCell align="center">
        {isEditing ? (
          <EditableTextField
            type="number"
            inputProps={{ step: "0.1", min: "0" }}
            value={localValue}
            onChange={handleMetricChange}
            onBlur={handleBlur}
            onKeyDown={(e) => e.key === "Enter" && handleBlur()}
            size="small"
            autoFocus
            aria-label={`${field} input`}
            sx={{
              "& .MuiInputBase-input": {
                padding: "8px",
                fontSize: "0.75rem",
                color: colors.grey[900],
                textAlign: "center",
              },
              width: "100px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: colors.grey[400],
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: colors.grey[500],
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: colors.primary.main,
                borderWidth: "2px",
              },
            }}
          />
        ) : (
          <ClickableSpan
            onClick={() => setEditingCell({ uuid, field })}
            onKeyDown={(e) => e.key === "Enter" && setEditingCell({ uuid, field })}
            tabIndex={0}
            role="button"
            aria-label={`Edit ${field}`}
          >
            {displayValue == null ? "-" : formatMetric(displayValue, driverMode)}
          </ClickableSpan>
        )}
      </RegularTableCell>
    );
  }
);

const RegularTableCell = styled(TableCell)<{ isEmpty?: boolean }>(({ theme, isEmpty }) => ({
  border: `1px solid ${colors.grey[300]}`,
  padding: theme.spacing(1.5),
  fontSize: "0.75rem",
  color: colors.text.primary,
  backgroundColor: isEmpty ? colors.grey[100] : colors.background.default,
  whiteSpace: "nowrap",
}));

const EditableTextField = styled(TextField)(({ theme }: { theme: Theme }) => ({
  "& .MuiInputBase-input": {
    textAlign: "center",
    fontSize: "0.75rem",
    color: colors.grey[900],
  },
  width: "100px",
}));

const ClickableSpan = styled("span")(({ theme }) => ({
  cursor: "pointer",
  padding: theme.spacing(2),
}));

export default DriverMetricCell;
